import type { ReactNode } from "react";
import { Network } from "lucide-react";
import { cn } from "@/lib/utils";
import { TOOLBOX_PAGE_TITLE, TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

/**
 * The signed-out screen inside SpecTree Toolbox (epic PI-SPEC-SW-E288, feature
 * PI-SPEC-SW-1463; UX-PM-36 and §8.6 Q9 in docs/analysis/toolbox-ux/03-evaluation.md;
 * Claude Design "Getting Started Mockups", frame 1a).
 *
 * Inside the app /login shows the Toolbox: its name and icon, what signing in
 * gives, the Microsoft button, the setup steps that follow and the app version,
 * instead of SpecTree's marketing page. The sign-in itself stays in LoginPage.
 */

/**
 * How long setup takes after sign-in. A target from §8.6 Q9, to be measured in
 * the dress rehearsal and corrected here (one place).
 */
export const TOOLBOX_SETUP_ESTIMATE = "about 10 minutes";

/** The setup steps that follow sign-in, in order: what, then how. */
export const TOOLBOX_SETUP_STEPS = [
  { title: "Connect Azure DevOps", detail: "paste one token" },
  { title: "Sign in to Claude", detail: "your Claude seat" },
  { title: "Get your project's code", detail: "automatic" },
] as const;

export interface ToolboxSignInProps {
  /** The app's version, from `bridge.app.version`. */
  version: string;
  /** Starts the Microsoft (Entra) sign-in. */
  onSignIn: () => void;
  /** True once the redirect to Microsoft has started. */
  isSigningIn?: boolean;
  /** Why sign-in could not start, if it failed. */
  error?: string | undefined;
  /**
   * The email and password form, only when local login is on (local
   * development); shown below the Microsoft button, as on the browser page.
   * Never passed in production.
   */
  localLogin?: ReactNode;
}

/** The four-square Microsoft logo. */
function MicrosoftLogo() {
  return (
    <svg className="h-[17.5px] w-[17.5px] flex-none" viewBox="0 0 17.5 17.5" aria-hidden="true">
      <rect x="0" y="0" width="8" height="8" fill="#F25022" />
      <rect x="9.5" y="0" width="8" height="8" fill="#7FBA00" />
      <rect x="0" y="9.5" width="8" height="8" fill="#00A4EF" />
      <rect x="9.5" y="9.5" width="8" height="8" fill="#FFB900" />
    </svg>
  );
}

export function ToolboxSignIn({ version, onSignIn, isSigningIn = false, error, localLogin }: ToolboxSignInProps) {
  return (
    <div
      data-testid="toolbox-sign-in"
      className="flex min-h-screen flex-col overflow-y-auto bg-background text-foreground"
    >
      {/*
        The window is frameless and draws no title bar here: the top 42 px are the drag
        region (as on the app's offline page), and the Windows controls sit over its right end.
      */}
      <div aria-hidden="true" className="fixed inset-x-0 top-0 z-10 h-[42px] [-webkit-app-region:drag]" />
      <div className="flex flex-1 justify-center px-6 pb-10 pt-[42px]">
        <main className="mt-[70px] flex w-full max-w-[440px] flex-col items-center text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-[15px] bg-[#E11837] shadow-[0_10px_30px_rgba(225,24,55,0.25)]">
            <Network className="h-8 w-8 text-white" aria-hidden="true" />
          </div>
          <h1 className={cn(TOOLBOX_PAGE_TITLE, "mt-[18px]")}>SpecTree Toolbox</h1>
          <p className="mb-[26px] mt-[10px] text-[14px] leading-[1.55] text-muted-foreground">
            {"Sign in with your Toro Microsoft account "}
            <br />
            {"to see your projects' backlogs."}
          </p>

          {error && (
            <p role="alert" className={cn(TOOLBOX_TEXT.content, "mb-3 text-red-400")}>
              {error}
            </p>
          )}

          <button
            type="button"
            onClick={onSignIn}
            disabled={isSigningIn}
            className="inline-flex h-10 w-[300px] max-w-full items-center justify-center gap-2.5 rounded-lg bg-[#FAFAFA] text-[14px] font-semibold text-[#18181B] transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-70 [-webkit-app-region:no-drag]"
          >
            {isSigningIn ? (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <MicrosoftLogo />
            )}
            {isSigningIn ? "Redirecting…" : "Sign in with Microsoft"}
          </button>

          {localLogin ? (
            <div className="mt-5 w-[300px] max-w-full text-left [-webkit-app-region:no-drag]" data-testid="toolbox-local-login">
              <div className="mb-4 flex items-center gap-3" aria-hidden="true">
                <div className="h-px flex-1 bg-white/[0.08]" />
                <span className="text-[11px] uppercase tracking-wider text-white/25">or, local development</span>
                <div className="h-px flex-1 bg-white/[0.08]" />
              </div>
              {localLogin}
            </div>
          ) : null}

          <section
            aria-labelledby="toolbox-sign-in-next"
            className="mt-[30px] w-full rounded-[10px] border border-[#1F1F23] bg-[#111113] px-[18px] py-4 text-left"
          >
            <div className="flex items-baseline justify-between gap-3">
              <h2 id="toolbox-sign-in-next" className="font-display text-[14px] font-semibold leading-[1.3]">
                After you sign in
              </h2>
              <span className={cn(TOOLBOX_TEXT.meta, TOOLBOX_TEXT.faint)}>{TOOLBOX_SETUP_ESTIMATE}</span>
            </div>
            <ol className="mt-1.5">
              {TOOLBOX_SETUP_STEPS.map((step, index) => (
                <li key={step.title} className={cn(TOOLBOX_TEXT.content, "flex items-center gap-3 py-[7px]")}>
                  <span
                    aria-hidden="true"
                    className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full border border-[#3F3F46] text-[11px] font-semibold text-muted-foreground"
                  >
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1">{step.title}</span>
                  <span className={cn(TOOLBOX_TEXT.meta, TOOLBOX_TEXT.faint)}>{step.detail}</span>
                </li>
              ))}
            </ol>
            {/* A full sentence: 13 px, not the mockup's 12 px (UX-PM-27, driver decision 2026-10-21). */}
            <p className={cn(TOOLBOX_TEXT.content, "mt-2 leading-[1.55] text-muted-foreground")}>
              Git and Claude Code install themselves if this computer doesn't have them. Nothing to type.
            </p>
          </section>

          <p className={cn(TOOLBOX_TEXT.meta, TOOLBOX_TEXT.faint, "mt-[22px]")}>Version {version}</p>
        </main>
      </div>
    </div>
  );
}
